import { Injectable } from '@angular/core';
import { NgControl } from '@angular/forms';

import type { Injector } from '@angular/core';
import type { AbstractControl } from '@angular/forms';

/**
 * Thin, lazy accessor for the `NgControl` associated with a custom form control.
 *
 * IMPORTANT — always resolve `NgControl` through this service rather than injecting it
 * eagerly. Angular Signal Forms' `FormField` directive provides `NgControl` via
 * `useFactory: () => inject(FormField).interopNgControl`, and `FormField` itself resolves
 * `NG_VALUE_ACCESSOR`. A control that self-registers as a value accessor and eagerly
 * injects `NgControl` therefore forms a structural dependency cycle that fails with
 * NG0200 under `[formField]`:
 *
 *   Component -> NgControl -> FormField -> NG_VALUE_ACCESSOR -> Component
 *
 * The `td-wb-rules/no-eager-ngcontrol-injection` lint rule enforces this.
 *
 * Note that under Signal Forms the resolved `NgControl` is an `InteropNgControl` shim.
 * It exposes read-only state (`value`, `valid`, `invalid`, `errors`, `touched`, ...) but
 * implements no mutating API: `markAsDirty`, `markAsTouched` and `setErrors` are absent
 * entirely, and `updateValueAndValidity()` is an empty no-op.
 *
 * Critically, `InteropNgControl.control` is assigned `this`, so it is never null. Guarding
 * an imperative call with `ngControl?.control?.markAsDirty()` therefore passes both
 * optional-chaining checks and then throws `TypeError`. Route reads and revalidation
 * through the helpers below instead of touching `NgControl.control` directly, and model
 * user interaction through `registerOnTouched()` rather than mutating the control.
 *
 * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md` (hazards H1, H2).
 */
@Injectable({ providedIn: 'root' })
export class SignalFormBridgeService {
  /** Resolve the owning `NgControl`, or `null` when the control is not inside a form. */
  getNgControl(injector: Injector): NgControl | null {
    return injector.get(NgControl, null, { optional: true, self: true });
  }

  /**
   * Request revalidation of the owning control.
   *
   * Intentionally a no-op under Signal Forms: validation there is derived from the schema,
   * so imperative revalidation is neither required nor supported.
   *
   * Takes an already-resolved `NgControl` so callers can cache the lazy lookup.
   */
  revalidateCurrentControl(ngControl: NgControl | null): void {
    const control = this.getCurrentControl(ngControl);
    if (control === null || this.isSignalFormsShim(control)) {
      return;
    }

    control.updateValueAndValidity();
  }

  /** Read-only validity. Safe under both form models — `invalid` is a real getter on the shim. */
  isCurrentControlInvalid(ngControl: NgControl | null): boolean {
    return this.getCurrentControl(ngControl)?.invalid ?? false;
  }

  /**
   * Returns the owning control **only when it supports mutation** — i.e. when it is a real
   * `AbstractControl` rather than the Signal Forms `InteropNgControl` shim.
   *
   * Use this to guard any imperative call (`setValidators`, `setErrors`, `markAsDirty`,
   * `events`, ...). Guarding on `ngControl?.control` alone is not enough: the shim assigns
   * `control = this`, so it is always truthy and the call then throws.
   *
   * Returning `null` under Signal Forms is correct rather than lossy — validation there is
   * schema-derived, so imperative validator injection has no meaning.
   */
  getMutableControl(ngControl: NgControl | null): AbstractControl | null {
    const control = this.getCurrentControl(ngControl);
    return control === null || this.isSignalFormsShim(control) ? null : control;
  }

  private getCurrentControl(ngControl: NgControl | null): AbstractControl | null {
    return ngControl?.control ?? null;
  }

  /**
   * Signal Forms supplies an `InteropNgControl` shim. It is reliably identified by the
   * absence of `markAsDirty`, which every real `AbstractControl` implements.
   */
  private isSignalFormsShim(control: AbstractControl): boolean {
    return typeof (control as Partial<AbstractControl>).markAsDirty !== 'function';
  }
}

